import { produce } from 'immer'

import { ChatState } from './chat.state'

import { FeedbackType, Message, MessageType } from '../../types/agent.dictionary'

const onGetAgentResponseStream = (state: ChatState, { response }: { response: any }): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.streamingAiResponse = true

    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    const { content, reasoning, chunk, type, error: _error, pass_to_human } = response
    if (_error) {
      return
    }
    if (type === 'ai_response') {
      draft.pendingRequestActionMessage = 'Preparing response.'
      if (isLastMessageOfTypeAi) {
        Object.assign(lastMessageDraft, response)
        lastMessageDraft.message = content

        lastMessageDraft.type = MessageType.AiMsg
      } else {
        draft.messages.push({
          ...response,
          message: content,
          pass_to_human: pass_to_human ?? false,
          type: MessageType.AiMsg,
        })
      }
    }
    if (type === 'reasoning') {
      draft.pendingRequestActionMessage = 'Reasoning.'
      if (isLastMessageOfTypeAi) {
        lastMessageDraft.reasoning = reasoning
      } else {
        draft.messages.push({
          ...response,
          reasoning,
          message: content,
          type: MessageType.AiMsg,
        })
      }
    }
    if (type === 'tool_call') {
      draft.pendingRequestActionMessage = 'Calling tool.'
      if (isLastMessageOfTypeAi) {
        lastMessageDraft.tool_calls = chunk
      } else {
        draft.messages.push({
          ...response,
          reasoning,
          message: content,
          tool_calls: chunk,
          type: MessageType.AiMsg,
        })
      }
    }
    if (type === 'tool_response') {
      draft.pendingRequestActionMessage = 'Tool Responded.'

      const updated_tool_calls = lastMessageDraft.tool_calls.map((toolCall) => {
        if (toolCall.id === chunk.tool_call_id) {
          toolCall.response = chunk.content
        }
        return toolCall
      })
      lastMessageDraft.tool_calls = updated_tool_calls
    }
    if (type === 'voice_audio') {
      draft.voiceEnabled = true
      draft.pendingRequestActionMessage = 'Playing audio response...'
      if (isLastMessageOfTypeAi) {
        lastMessageDraft.audio_url = response.audio_url
      } else {
        draft.messages.push({
          ...response,
          message: content,
          audio_url: response.audio_url,
          type: MessageType.AiMsg,
        })
      }
    }
  })

const onUserSendMessage = (
  state: ChatState,
  { message, agentId, image }: { message: string; agentId: string; image?: string }
): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.pendingAiResponse = true
    draft.agentId = agentId
    draft.previousUserMessage = message
    draft.messages.push({
      message,
      type: MessageType.UserMsg,
      ...(image ? { image } : {}),
    })
  })

const onGetAgentResponseFailure = (state: ChatState): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.pendingAiResponse = false
  })

const onGetAgentResponseStreamCompleted = (state: ChatState): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.pendingAiResponse = false
  })
const onSendFeedback = (
  state: ChatState,
  { feedback, message }: { feedback: FeedbackType; message: Message }
): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.messages = draft.messages.map((msg) =>
      msg.id === message.id ? { ...msg, feedback } : msg
    )
  })

const onRedoMessage = (state: ChatState, { message }: { message: Message }): ChatState =>
  produce(state, (draft: ChatState) => {
    for (let i = draft.messages.length - 1; i >= 0; i--) {
      const msg = draft.messages[i]
      if (msg && msg.type === MessageType.AiMsg && msg.id === message.id) {
        draft.messages.splice(i, 1)
        break
      }
    }
  })

const onClearMessages = (state: ChatState): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.messages = []
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
    draft.previousUserMessage = null
    draft.agentId = null
    draft.voiceEnabled = false
    draft.isRecording = false
  })

const onAddInitialMessage = (
  state: ChatState,
  { initialMessage }: { initialMessage: string }
): ChatState =>
  produce(state, (draft: ChatState) => {
    draft.messages.push({ message: initialMessage, type: MessageType.AiMsg })
  })

export {
  onGetAgentResponseStream,
  onUserSendMessage,
  onGetAgentResponseFailure,
  onGetAgentResponseStreamCompleted,
  onSendFeedback,
  onRedoMessage,
  onClearMessages,
  onAddInitialMessage,
}
